import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";

// /admin/generate-jd — dedicated JD generator backed by POST /ai/jobs/generate-jd.
// Mirrors the inline form on /admin/create-job but gives the flow its own URL,
// preview pane, and "Use this JD" hand-off (localStorage 'ample_pending_jd').

const SENIORITY_OPTIONS = ["Entry", "Mid", "Senior", "Lead", "Principal"];
const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const TONE_OPTIONS = ["Professional", "Casual", "Startup", "Enterprise", "Inclusive"];

const seniorityToYoeRange = (s) => {
  switch (s) {
    case "Entry":     return { min: 0, max: 2 };
    case "Mid":       return { min: 2, max: 5 };
    case "Senior":    return { min: 5, max: 10 };
    case "Lead":      return { min: 8, max: 15 };
    case "Principal": return { min: 12, max: 20 };
    default:          return { min: 0, max: 5 };
  }
};

const PENDING_JD_KEY = "ample_pending_jd";

export default function GenerateJd() {
  const nav = useNavigate();
  const [form, setForm] = useState(() => ({
    title: "",
    seniority: "Senior",
    employmentType: "Full-time",
    location: "",
    remote: false,
    hybrid: false,
    yoeMin: 5,
    yoeMax: 10,
    mustHave: [],
    niceToHave: [],
    tone: "Professional",
    companyContext: "",
  }));
  const [mustHaveInput, setMustHaveInput] = useState("");
  const [niceToHaveInput, setNiceToHaveInput] = useState("");
  const [jd, setJd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => { document.title = "Generate JD - Ample Hire"; }, []);

  const canGenerate = useMemo(
    () => form.title.trim().length > 0 && form.seniority && form.mustHave.length > 0,
    [form.title, form.seniority, form.mustHave]
  );

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Keep the YOE slider in sync with seniority unless the user has manually
  // changed it. Cheap heuristic: snap to the seniority's typical band when
  // they pick a new seniority.
  const setSeniority = (s) => {
    const range = seniorityToYoeRange(s);
    setForm((f) => ({ ...f, seniority: s, yoeMin: range.min, yoeMax: range.max }));
  };

  const addChip = (kind, raw) => {
    const v = String(raw || "").trim();
    if (!v) return;
    setForm((f) => {
      const next = f[kind].includes(v) ? f[kind] : [...f[kind], v];
      return { ...f, [kind]: next };
    });
    if (kind === "mustHave") setMustHaveInput("");
    else setNiceToHaveInput("");
  };
  const removeChip = (kind, v) =>
    setForm((f) => ({ ...f, [kind]: f[kind].filter((x) => x !== v) }));

  const buildRequestBody = () => {
    const locations = [];
    if (form.location.trim()) locations.push(form.location.trim());
    if (form.remote) locations.push("Remote");
    if (form.hybrid) locations.push("Hybrid");
    return {
      title: form.title.trim(),
      hiringType: form.employmentType.toLowerCase().includes("intern")
        ? "campus" : "lateral",
      locations,
      tone: form.tone,
      openings: 1,
      minimumExperience: String(form.yoeMin),
      maximumExperience: String(form.yoeMax),
      domainSkills: form.mustHave.join(", "),
      softSkills: form.niceToHave.join(", "),
    };
  };

  const generate = async () => {
    if (!canGenerate || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.post("/ai/jobs/generate-jd", buildRequestBody());
      if (!data || !data.overview) throw new Error("Invalid JD response from server");
      setJd(data);
    } catch (e) {
      setError(e?.message || e?.data?.error || "Failed to generate JD");
    } finally {
      setLoading(false);
    }
  };

  const jdAsMarkdown = useMemo(() => {
    if (!jd) return "";
    const parts = [];
    parts.push(`# ${jd.title || form.title}\n`);
    if (jd.overview) parts.push(jd.overview + "\n");
    if (jd.responsibilities?.length) {
      parts.push("## Responsibilities\n" + jd.responsibilities.map((r) => `- ${r}`).join("\n") + "\n");
    }
    if (jd.requirements?.length) {
      parts.push("## Requirements\n" + jd.requirements.map((r) => `- ${r}`).join("\n") + "\n");
    }
    if (jd.perks?.length) {
      parts.push("## Perks\n" + jd.perks.map((r) => `- ${r}`).join("\n") + "\n");
    }
    return parts.join("\n");
  }, [jd, form.title]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(jdAsMarkdown);
      setToast("Copied to clipboard");
      setTimeout(() => setToast(null), 1500);
    } catch {
      setToast("Couldn't copy — clipboard blocked");
      setTimeout(() => setToast(null), 2000);
    }
  };

  const useThisJd = () => {
    if (!jd) return;
    // The create-job page reads this key on mount, prefills, then clears it.
    sessionStorage.setItem(PENDING_JD_KEY, JSON.stringify({
      title: form.title,
      jd,
      mustHave: form.mustHave,
      niceToHave: form.niceToHave,
      employmentType: form.employmentType,
      location: form.location,
      remote: form.remote,
      hybrid: form.hybrid,
    }));
    nav("/admin/create-job");
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Generate JD</h1>
          <p className="text-sm text-vf-muted mt-1">
            Describe the role; we'll draft a JD you can edit, copy, or carry into Create position.
          </p>
        </div>
        <button onClick={() => nav("/admin/create-job")} className="px-3 py-1.5 rounded border border-slate-300 text-xs">
          Skip to Create position →
        </button>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* ── Form column ─────────────────────────────────────────────── */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          <Section title="Role">
            <Field label="Job title" required>
              <input
                type="text"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="Senior Backend Engineer"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
            </Field>
            <Field label="Seniority" required>
              <SelectChips
                options={SENIORITY_OPTIONS}
                value={form.seniority}
                onChange={setSeniority}
              />
            </Field>
            <Field label="Employment type">
              <SelectChips
                options={EMPLOYMENT_TYPES}
                value={form.employmentType}
                onChange={(v) => update("employmentType", v)}
              />
            </Field>
          </Section>

          <Section title="Location">
            <Field label="Office location">
              <input
                type="text"
                value={form.location}
                onChange={(e) => update("location", e.target.value)}
                placeholder="Bangalore, India"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
            </Field>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.remote} onChange={(e) => update("remote", e.target.checked)} />
                Remote
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.hybrid} onChange={(e) => update("hybrid", e.target.checked)} />
                Hybrid
              </label>
            </div>
          </Section>

          <Section title="Experience">
            <Field label={`${form.yoeMin}–${form.yoeMax} years`}>
              <div className="flex gap-3 items-center">
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={form.yoeMin}
                  onChange={(e) => update("yoeMin", Math.min(Number(e.target.value), form.yoeMax))}
                  className="flex-1"
                />
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={form.yoeMax}
                  onChange={(e) => update("yoeMax", Math.max(Number(e.target.value), form.yoeMin))}
                  className="flex-1"
                />
              </div>
            </Field>
          </Section>

          <Section title="Skills">
            <Field label="Must-have skills" required>
              <ChipInput
                value={mustHaveInput}
                onChange={setMustHaveInput}
                onCommit={(v) => addChip("mustHave", v)}
                placeholder="Type a skill and press Enter"
              />
              <ChipList items={form.mustHave} onRemove={(v) => removeChip("mustHave", v)} />
            </Field>
            <Field label="Nice-to-have skills">
              <ChipInput
                value={niceToHaveInput}
                onChange={setNiceToHaveInput}
                onCommit={(v) => addChip("niceToHave", v)}
                placeholder="Add a soft skill or bonus area"
              />
              <ChipList items={form.niceToHave} onRemove={(v) => removeChip("niceToHave", v)} />
            </Field>
          </Section>

          <Section title="Style">
            <Field label="Tone">
              <SelectChips
                options={TONE_OPTIONS}
                value={form.tone}
                onChange={(v) => update("tone", v)}
              />
            </Field>
            <Field label="Company context (optional)">
              <textarea
                value={form.companyContext}
                onChange={(e) => update("companyContext", e.target.value)}
                rows={3}
                placeholder="Anything worth knowing — culture, mission, team makeup"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
              <div className="text-[10px] text-vf-muted mt-1">
                Not sent to the model yet — included in the request as a hint when supported.
              </div>
            </Field>
          </Section>

          <button
            onClick={generate}
            disabled={!canGenerate || loading}
            className="w-full px-4 py-2.5 rounded bg-vf-primary text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Generating…" : jd ? "↻ Regenerate" : "✨ Generate JD"}
          </button>
          {error && (
            <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700">{error}</div>
          )}
        </div>

        {/* ── Preview column ──────────────────────────────────────────── */}
        <div className="col-span-12 lg:col-span-7">
          <div className="sticky top-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">Preview</div>
              {jd && (
                <div className="flex gap-1.5">
                  <button onClick={copy} className="px-2 py-1 rounded border border-slate-300 text-xs">📋 Copy</button>
                  <button onClick={generate} disabled={loading} className="px-2 py-1 rounded border border-slate-300 text-xs disabled:opacity-50">↻ Regenerate</button>
                  <button onClick={useThisJd} className="px-2 py-1 rounded bg-vf-primary text-white text-xs font-semibold">✓ Use this JD</button>
                </div>
              )}
            </div>
            <div className="p-5 max-h-[75vh] overflow-y-auto">
              {loading && <SkeletonJd />}
              {!loading && !jd && <EmptyPreview />}
              {!loading && jd && <RenderedJd jd={jd} title={form.title} />}
            </div>
          </div>
          {toast && (
            <div className="mt-3 text-center text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded py-1.5">
              {toast}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs text-vf-muted mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

function SelectChips({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`px-2.5 py-1 rounded-full text-xs border ${
            value === o
              ? "bg-vf-primary text-white border-vf-primary"
              : "bg-white text-vf-base border-slate-300 hover:border-slate-400"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function ChipInput({ value, onChange, onCommit, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === ",") {
          e.preventDefault();
          onCommit(value);
        }
      }}
      onBlur={() => value.trim() && onCommit(value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
    />
  );
}

function ChipList({ items, onRemove }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {items.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-vf-tint/40 text-vf-base text-xs">
          {v}
          <button
            type="button"
            onClick={() => onRemove(v)}
            className="text-vf-muted hover:text-red-600"
            aria-label={`Remove ${v}`}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

function EmptyPreview() {
  return (
    <div className="text-center text-sm text-vf-muted py-16">
      <div className="text-3xl mb-2">📝</div>
      <div className="font-medium text-vf-base mb-1">Fill in the form and click Generate</div>
      <div>Your draft JD will appear here once it's ready.</div>
    </div>
  );
}

function SkeletonJd() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-6 bg-slate-200 rounded w-2/3" />
      <div className="h-3 bg-slate-200 rounded w-full" />
      <div className="h-3 bg-slate-200 rounded w-11/12" />
      <div className="h-3 bg-slate-200 rounded w-10/12" />
      <div className="h-4 bg-slate-300 rounded w-1/3 mt-4" />
      <div className="h-3 bg-slate-200 rounded w-full" />
      <div className="h-3 bg-slate-200 rounded w-full" />
      <div className="h-3 bg-slate-200 rounded w-3/4" />
    </div>
  );
}

function RenderedJd({ jd, title }) {
  return (
    <div className="space-y-5 text-sm text-vf-base leading-relaxed">
      <h2 className="text-xl font-bold text-vf-base">{jd.title || title}</h2>
      {jd.overview && <p className="whitespace-pre-line">{jd.overview}</p>}
      {jd.responsibilities?.length > 0 && (
        <Block heading="Responsibilities" items={jd.responsibilities} />
      )}
      {jd.requirements?.length > 0 && (
        <Block heading="Requirements" items={jd.requirements} />
      )}
      {jd.perks?.length > 0 && (
        <Block heading="Perks" items={jd.perks} />
      )}
    </div>
  );
}

function Block({ heading, items }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-2">{heading}</div>
      <ul className="list-disc pl-5 space-y-1.5">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
